import React from 'react';
import { UploadProps } from 'antd/lib/upload';
import {
  Button,
  Card,
  Icon,
  Upload,
  UploadFileType,
  UploadChangeParamType,
  FileList,
  message,
} from 'components';
import {
  PhotoFileType,
  PhotoFileStatus,
  AttachmentsType,
  UpdateFileType,
} from '../types';
import { StyledDragger } from './StyledComponents';

const DRAGGER_TITLE = 'Click or drag an attachment to this area to upload';

const DRAGGER_DESCRIPTION =
  'You may upload single or multiple files at a time. Currently supporting .pdf, .docx, .xlsx, .jpeg, and .png filetypes.';

const DRAGGER_FILE_FORMATS = [
  '.pdf',
  '.docx',
  '.jpeg',
  '.jpg',
  '.png',
  '.xlsx',
];

interface PublicAttachmentsProps {
  attachments: AttachmentsType;
  updateFile: UpdateFileType;
}

const PublicAttachments: React.FC<PublicAttachmentsProps> = ({
  attachments,
  updateFile,
}) => {
  const onChange = ({
    file,
    fileList,
  }: UploadChangeParamType<UploadFileType<any>>) => {
    let fileStatus: PhotoFileStatus = 'uploading';
    if (
      file.response &&
      (file.response.status === 'error' || file.error?.status >= 400)
    ) {
      const errorDetails =
        file.response?.message || file.response || 'Upload Error';
      fileStatus = 'error';
      const errorFile = fileList.find(({ uid }) => uid === file.uid);
      if (errorFile) errorFile.status = 'error';
      message.error(`Error uploading file: ${file.name}. ${errorDetails}`);
    } else if (file.status === 'done' && file.response?.url) {
      fileStatus = 'done';
    }
    updateFile(file.uid, {
      filename: file.name,
      mimetype: file.type,
      url: file.response?.url,
      size: file.size,
      status: fileStatus,
      updatedAt: file.lastModifiedDate,
      percent: file.percent,
    });
  };

  const removeFile = (file: PhotoFileType & { uid: string }) => {
    updateFile(file.uid, {
      ...file,
      isDeleted: true,
      status: 'removed',
    });
  };

  const uploadProps: Partial<UploadProps> = {
    onChange,
    accept: DRAGGER_FILE_FORMATS.join(','),
    action: '/upload/single',
    showUploadList: false,
    multiple: true,
  };

  return (
    <Card
      type="primary"
      title="Public Attachments"
      extra={
        attachments.length ? (
          <Upload {...uploadProps}>
            <Button>
              <Icon type="antdUploadOutlined" /> Upload Attachment
            </Button>
          </Upload>
        ) : null
      }
      id="attachments"
    >
      <StyledDragger
        {...uploadProps}
        title={!attachments.length && DRAGGER_TITLE}
        description={!attachments.length && DRAGGER_DESCRIPTION}
        showIcon={!attachments.length}
        openFileDialogOnClick={!attachments.length}
        $maxWidth={!attachments.length && '400px'}
      >
        {!attachments.length ? (
          <Button>
            <Icon type="antdUploadOutlined" /> Upload Attachment
          </Button>
        ) : (
          <FileList files={attachments} onRemoveFile={removeFile} />
        )}
      </StyledDragger>
    </Card>
  );
};

export default React.memo(PublicAttachments);
